// src/components/Admin/Cantieri/ReportCantieriManagement.jsx - CON SPESE VARIE INTEGRATE
import React, { useState, useEffect } from 'react';
import { 
  getCantieri, 
  getRapportiniByCantiereId, 
  getAllUsers, 
  getMezziAttrezzature, 
  getMaterialiCostoVariabile, 
  getMaterialiCostoFisso, 
  getLavorazioni 
} from '../../../firebase';
import { getCostoPersona, toNumber } from '../../../utils/costiUtils';
import logo from '../../../assets/logo.png';

// FORMATTAZIONE ITALIANA DEI NUMERI (11.111,00)
const formatCurrency = (number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0,00';
  }
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const ReportCantieriManagement = () => {
  const [cantieri, setCantieri] = useState([]);
  const [selectedCantiere, setSelectedCantiere] = useState('');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rapportini, setRapportini] = useState([]);
  const [users, setUsers] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [materialiVariabili, setMaterialiVariabili] = useState([]);
  const [materialiFissi, setMaterialiFissi] = useState([]);
  const [lavorazioni, setLavorazioni] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const loadXLSX = () => {
      if (!window.XLSX) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        script.async = true;
        script.onload = () => { console.log('XLSX caricato'); };
        document.head.appendChild(script);
      }
    };
    loadXLSX();
  }, []);

  const loadInitialData = async () => {
    try {
      const [cantieriData, usersData, mezziData] = await Promise.all([
        getCantieri(),
        getAllUsers(),
        getMezziAttrezzature()
      ]);
      setCantieri(cantieriData);
      setUsers(usersData);
      setMezzi(mezziData);
    } catch (error) {
      console.error('Errore nel caricamento dati iniziali:', error);
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
  };

  const filteredCantieri = cantieri.filter(c =>
    (c.nomeCommessa || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.luogo || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const caricaRapportini = async () => {
    if (!selectedCantiere || !dataInizio || !dataFine) {
      showNotification('Seleziona cantiere e range di date', 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const rapportiniData = await getRapportiniByCantiereId(selectedCantiere);

      const filteredRapportini = rapportiniData.filter(rapportino => {
        const dataRapportino = new Date(rapportino.data);
        const inizio = new Date(dataInizio);
        const fine = new Date(dataFine);
        return dataRapportino >= inizio && dataRapportino <= fine;
      });

      setRapportini(filteredRapportini);
      elaborateReportData(filteredRapportini);
      showNotification(`Caricati ${filteredRapportini.length} rapportini`, 'success');
    } catch (error) {
      console.error('Errore nel caricamento rapportini:', error);
      showNotification('Errore nel caricamento rapportini', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // ELABORAZIONE DATI — include spese varie con allocazione
  // ============================================================
  const elaborateReportData = (rapportiniData) => {
    const cantiere = cantieri.find(c => c.id === selectedCantiere);
    if (!cantiere) return;

    const personaleMap = new Map();
    const lavorazioniMap = new Map();
    const materialiVariabiliList = [];
    const materialiFissiList = [];
    const mezziUtilizzatiMap = new Map();
    const speseVarieList = []; // ✨ Lista spese non categorizzate
    let totaleRicavi = 0;

    rapportiniData.forEach((rapportino) => {
      // Personale
      (rapportino.personalePresente || []).forEach(persona => {
        const key = `${persona.nome} ${persona.cognome}`;
        if (!personaleMap.has(key)) {
          const user = users.find(u =>
            (u.id === persona.userId || u.id === persona.id) ||
            (u.nome === persona.nome && u.cognome === persona.cognome)
          );
          personaleMap.set(key, {
            nome: persona.nome,
            cognome: persona.cognome,
            incarico: user?.incarico || persona.incarico || 'N/D',
            totalOre: 0,
            totalCosto: 0,
            costoOrario: persona.costoOrario || 0
          });
        }
        const existing = personaleMap.get(key);
        existing.totalOre += toNumber(persona.oreLavorate);
        existing.totalCosto += getCostoPersona(persona);
      });

      // Lavorazioni
      (rapportino.lavorazioni || []).forEach(lavorazione => {
        const key = lavorazione.nomeLavorazione || lavorazione.descrizione;
        if (!lavorazioniMap.has(key)) {
          lavorazioniMap.set(key, {
            nome: key,
            quantitaTotale: 0,
            descrizioni: new Set()
          });
        }
        const existing = lavorazioniMap.get(key);
        existing.quantitaTotale += toNumber(lavorazione.quantita);
        if (lavorazione.descrizione) {
          existing.descrizioni.add(lavorazione.descrizione);
        }
      });

      // Materiali variabili
      (rapportino.materialiVariabili || []).forEach(materiale => {
        materialiVariabiliList.push({
          nome: materiale.nomeMacrovoce || materiale.descrizione,
          descrizione: materiale.descrizione,
          costo: toNumber(materiale.costo),
          data: rapportino.data
        });
      });

      // Materiali fissi
      (rapportino.materialiFissi || []).forEach(materiale => {
        materialiFissiList.push({
          nome: materiale.nomeMateriale,
          quantita: toNumber(materiale.quantita),
          prezzoUnitario: toNumber(materiale.prezzoUnitario),
          costoTrasporto: toNumber(materiale.costoTrasporto),
          totale: toNumber(materiale.totale),
          data: rapportino.data
        });
      });

      // Mezzi
      (rapportino.mezziUtilizzati || []).forEach(mezzo => {
        const nomeMezzo = mezzo.nomeMezzo || mezzo.nome || 'Mezzo senza nome';
        const targa = mezzo.targa || '';
        const key = targa ? `${nomeMezzo} (${targa})` : nomeMezzo;
        if (!mezziUtilizzatiMap.has(key)) {
          // Proprietà presa dall'anagrafica mezzi (master data), non dal rapportino:
          // riflette sempre la proprietà attuale del mezzo
          const mezzoMaster = mezzi.find(m => m.id === mezzo.mezzoId) ||
            mezzi.find(m => (m.nomeMezzo || m.nome) === nomeMezzo && (m.targa || '') === targa);
          mezziUtilizzatiMap.set(key, {
            nome: nomeMezzo,
            targa: targa,
            nomeCompleto: key,
            proprieta: mezzoMaster?.proprieta || mezzo.proprieta || 'N/D',
            totalOre: 0,
            totalCosto: 0,
            costoOrario: toNumber(mezzo.costoOrario)
          });
        }
        const existing = mezziUtilizzatiMap.get(key);
        existing.totalOre += toNumber(mezzo.ore || mezzo.oreLavorate);
        existing.totalCosto += toNumber(mezzo.totale);
      });

      // Ricavi
      (rapportino.ricavi || []).forEach(ricavo => {
        totaleRicavi += toNumber(ricavo.importo);
      });
      (rapportino.lavorazioni || []).forEach(lavorazione => {
        totaleRicavi += toNumber(lavorazione.totale);
      });

      // ✨ SPESE VARIE — allocate per categoria
      (rapportino.speseVarie || []).forEach(spesa => {
        const importo = toNumber(spesa.importo);
        if (importo <= 0) return;
        const descrizione = spesa.descrizione || 'Spesa varia';
        const categoria = spesa.categoria || 'Spese Varie';

        if (categoria === 'Personale') {
          // Aggiunge come voce speciale nella tabella personale
          const key = `💳_spesa_personale_${descrizione}_${rapportino.id || Math.random()}`;
          personaleMap.set(key, {
            nome: '💳 ' + descrizione,
            cognome: '',
            incarico: 'Spesa Varia',
            totalOre: 0,
            totalCosto: importo,
            costoOrario: 0,
            isSpeseVarie: true,
            dataSpesa: rapportino.data
          });
        } else if (categoria === 'Mezzi') {
          const key = `💳_spesa_mezzi_${descrizione}_${rapportino.id || Math.random()}`;
          mezziUtilizzatiMap.set(key, {
            nome: '💳 ' + descrizione,
            targa: '',
            nomeCompleto: '💳 ' + descrizione,
            proprieta: '—',
            totalOre: 0,
            totalCosto: importo,
            costoOrario: 0,
            isSpeseVarie: true,
            dataSpesa: rapportino.data
          });
        } else if (categoria === 'Materiale') {
          materialiVariabiliList.push({
            nome: '💳 ' + descrizione,
            descrizione: descrizione,
            costo: importo,
            data: rapportino.data,
            isSpeseVarie: true
          });
        } else {
          // categoria === 'Spese Varie' → lista separata
          speseVarieList.push({
            descrizione: descrizione,
            importo: importo,
            data: rapportino.data
          });
        }
      });
    });

    const totalePersonale = Array.from(personaleMap.values()).reduce((sum, p) => sum + p.totalCosto, 0);
    const totaleMezzi = Array.from(mezziUtilizzatiMap.values()).reduce((sum, m) => sum + m.totalCosto, 0);
    const totaleMaterialiVariabili = materialiVariabiliList.reduce((sum, m) => sum + m.costo, 0);
    const totaleMaterialiFissi = materialiFissiList.reduce((sum, m) => sum + m.totale, 0);
    const totaleSpeseVarie = speseVarieList.reduce((sum, s) => sum + s.importo, 0); // ✨ NUOVO
    const totaleCosti = totalePersonale + totaleMezzi + totaleMaterialiVariabili + totaleMaterialiFissi + totaleSpeseVarie;
    const margine = totaleRicavi - totaleCosti;

    setReportData({
      cantiere,
      periodo: { inizio: dataInizio, fine: dataFine },
      personale: Array.from(personaleMap.values()),
      lavorazioni: Array.from(lavorazioniMap.values()),
      materialiVariabili: materialiVariabiliList,
      materialiFissi: materialiFissiList,
      mezzi: Array.from(mezziUtilizzatiMap.values()),
      speseVarie: speseVarieList, // ✨ NUOVO
      ricavi: rapportiniData.flatMap(r => r.ricavi || []),
      totali: {
        ricavi: totaleRicavi,
        personale: totalePersonale,
        mezzi: totaleMezzi,
        materialiVariabili: totaleMaterialiVariabili,
        materialiFissi: totaleMaterialiFissi,
        speseVarie: totaleSpeseVarie, // ✨ NUOVO
        costi: totaleCosti,
        margine: margine,
        marginePercentuale: totaleRicavi > 0 ? (margine / totaleRicavi * 100) : 0
      }
    });
  };

  const generaGiornaleLavori = () => {
    if (!reportData) return;
    const content = generateGiornaleLavoriContent();
    const nomeCantiere = reportData.cantiere.nomeCommessa || 'Cantiere';
    downloadPDF(content, `Giornale_Lavori_${nomeCantiere}_${reportData.periodo.inizio}_${reportData.periodo.fine}.pdf`);
  };

  const generaContabilita = () => {
    if (!reportData) return;
    const content = generateContabilitaContent();
    const nomeCantiere = reportData.cantiere.nomeCommessa || 'Cantiere';
    downloadPDF(content, `Contabilita_${nomeCantiere}_${reportData.periodo.inizio}_${reportData.periodo.fine}.pdf`);
  };

  const generaExcel = () => {
    if (!reportData) return;
    const nomeCantiere = reportData.cantiere.nomeCommessa || 'Cantiere';
    downloadExcelFile(reportData, `Report_Completo_${nomeCantiere}_${reportData.periodo.inizio}_${reportData.periodo.fine}.xlsx`);
  };

  const getLogoBase64 = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = this.width;
        canvas.height = this.height;
        ctx.drawImage(this, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = logo;
    });
  };

  // ============================================================
  // GIORNALE DEI LAVORI (PDF) — righe spese varie con sfondo arancione
  // ============================================================
  const generateGiornaleLavoriContent = () => {
    const { cantiere, periodo, personale, lavorazioni, materialiVariabili, materialiFissi, mezzi } = reportData;

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 15px; line-height: 1.4;">
        <div style="display: flex; align-items: center; margin-bottom: 20px; border-bottom: 3px solid #000; padding-bottom: 15px;">
          <img src="${logo}" alt="Logo" style="height: 90px; margin-right: 20px;" />
          <h1 style="color: #000; margin: 0; flex: 1; text-align: center; font-size: 24px; font-weight: bold;">
            GIORNALE DEI LAVORI
          </h1>
        </div>

        <div style="margin: 15px 0; font-size: 13px; background: #f5f5f5; padding: 15px; border-radius: 8px; border: 1px solid #ccc;">
          <p style="margin: 5px 0;"><strong style="font-size: 14px;">Nome Cantiere:</strong> ${cantiere.nomeCommessa || 'N/A'}</p>
          ${cantiere.contratto ? `<p style="margin: 5px 0;"><strong>Contratto:</strong> ${cantiere.contratto}</p>` : ''}
          ${cantiere.luogo ? `<p style="margin: 5px 0;"><strong>Luogo:</strong> ${cantiere.luogo}</p>` : ''}
          ${cantiere.impresa ? `<p style="margin: 5px 0;"><strong>Impresa:</strong> ${cantiere.impresa}</p>` : ''}
          <p style="margin: 5px 0;"><strong>Periodo:</strong> dal ${new Date(periodo.inizio).toLocaleDateString('it-IT')} al ${new Date(periodo.fine).toLocaleDateString('it-IT')}</p>
        </div>

        ${personale.length > 0 ? `
        <div style="margin: 20px 0;">
          <h2 style="color: #000; font-size: 16px; margin: 10px 0; border-bottom: 2px solid #000; padding-bottom: 5px;">PERSONALE IMPIEGATO</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #d0d0d0;">
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Dipendente</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Incarico</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: center;">Ore Totali</th>
              </tr>
            </thead>
            <tbody>
              ${personale.map(p => {
                if (p.isSpeseVarie) {
                  return `
                    <tr style="background: #fff3e0;">
                      <td style="border: 1px solid #999; padding: 6px; font-weight: bold;">${p.nome}</td>
                      <td style="border: 1px solid #999; padding: 6px; color: #999; font-style: italic;">Spesa Varia</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: center; color: #999;">—</td>
                    </tr>`;
                }
                return `
                  <tr>
                    <td style="border: 1px solid #999; padding: 6px;">${p.cognome} ${p.nome}</td>
                    <td style="border: 1px solid #999; padding: 6px;">${p.incarico}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: center;">${formatCurrency(p.totalOre)}</td>
                  </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${mezzi && mezzi.length > 0 ? `
        <div style="margin: 20px 0;">
          <h2 style="color: #000; font-size: 16px; margin: 20px 0; border-bottom: 2px solid #000; padding-bottom: 5px;">MEZZI E ATTREZZATURE</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #d0d0d0;">
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Descrizione</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Targa</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: center;">Ore Utilizzo</th>
              </tr>
            </thead>
            <tbody>
              ${mezzi.map(m => {
                if (m.isSpeseVarie) {
                  return `
                    <tr style="background: #fff3e0;">
                      <td style="border: 1px solid #999; padding: 6px; font-weight: bold;">${m.nome}</td>
                      <td style="border: 1px solid #999; padding: 6px; color: #999;">—</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: center; color: #999;">—</td>
                    </tr>`;
                }
                return `
                  <tr>
                    <td style="border: 1px solid #999; padding: 6px;">${m.nome}</td>
                    <td style="border: 1px solid #999; padding: 6px;">${m.targa || 'N/A'}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: center;">${formatCurrency(m.totalOre || 0)}</td>
                  </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${lavorazioni.length > 0 ? `
        <div style="margin: 20px 0;">
          <h2 style="color: #000; font-size: 16px; margin: 20px 0; border-bottom: 2px solid #000; padding-bottom: 5px;">LAVORAZIONI EFFETTUATE</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #d0d0d0;">
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Lavorazione</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: center;">Quantità</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Descrizioni</th>
              </tr>
            </thead>
            <tbody>
              ${lavorazioni.map(l => `
                <tr>
                  <td style="border: 1px solid #999; padding: 6px;">${l.nome}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${l.quantitaTotale ? formatCurrency(l.quantitaTotale) : '-'}</td>
                  <td style="border: 1px solid #999; padding: 6px;">${Array.from(l.descrizioni).join(', ')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${(materialiVariabili.length > 0 || materialiFissi.length > 0) ? `
        <div style="margin: 20px 0;">
          <h2 style="color: #000; font-size: 16px; margin: 20px 0; border-bottom: 2px solid #000; padding-bottom: 5px;">MATERIALI UTILIZZATI</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #d0d0d0;">
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Materiale</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: left;">Descrizione</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: center;">Quantità</th>
                <th style="border: 1px solid #666; padding: 8px; text-align: center;">Data</th>
              </tr>
            </thead>
            <tbody>
              ${materialiVariabili.map(m => `
                <tr ${m.isSpeseVarie ? 'style="background: #fff3e0;"' : ''}>
                  <td style="border: 1px solid #999; padding: 6px; ${m.isSpeseVarie ? 'font-weight: bold;' : ''}">${m.nome}</td>
                  <td style="border: 1px solid #999; padding: 6px;">${m.descrizione || '-'}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${m.isSpeseVarie ? '—' : '-'}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${new Date(m.data).toLocaleDateString('it-IT')}</td>
                </tr>
              `).join('')}
              ${materialiFissi.map(m => `
                <tr>
                  <td style="border: 1px solid #999; padding: 6px;">${m.nome}</td>
                  <td style="border: 1px solid #999; padding: 6px;">-</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${formatCurrency(m.quantita)}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${new Date(m.data).toLocaleDateString('it-IT')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${(reportData.speseVarie || []).length > 0 ? `
        <div style="margin: 20px 0;">
          <h2 style="color: #e65100; font-size: 16px; margin: 20px 0; border-bottom: 2px solid #e65100; padding-bottom: 5px;">💳 ALTRE SPESE VARIE</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #fff3e0;">
                <th style="border: 1px solid #999; padding: 8px; text-align: left;">Descrizione</th>
                <th style="border: 1px solid #999; padding: 8px; text-align: center;">Data</th>
              </tr>
            </thead>
            <tbody>
              ${(reportData.speseVarie || []).map(s => `
                <tr style="background: #fffde7;">
                  <td style="border: 1px solid #999; padding: 6px; font-weight: bold;">💳 ${s.descrizione}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${new Date(s.data).toLocaleDateString('it-IT')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        <div class="page-footer">
          <p style="margin: 0; font-size: 10px;">Documento generato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT')}</p>
        </div>
      </div>
    `;
  };

  // ============================================================
  // CONTABILITÀ (PDF) — include spese varie nel riepilogo
  // ============================================================
  const generateContabilitaContent = () => {
    const { cantiere, periodo, totali, personale, mezzi, materialiVariabili, materialiFissi } = reportData;

    const materialiVariabiliGrouped = materialiVariabili.reduce((acc, materiale) => {
      const nome = materiale.nome;
      if (acc[nome]) {
        acc[nome].costoTotale += materiale.costo;
      } else {
        acc[nome] = { nome, costoTotale: materiale.costo };
      }
      return acc;
    }, {});

    const materialiFissiGrouped = materialiFissi.reduce((acc, materiale) => {
      const nome = materiale.nome;
      if (acc[nome]) {
        acc[nome].quantitaTotale += materiale.quantita;
        acc[nome].totale += materiale.totale;
      } else {
        acc[nome] = { nome, quantitaTotale: materiale.quantita, totale: materiale.totale };
      }
      return acc;
    }, {});

    const materialiCombinati = [
      ...Object.values(materialiVariabiliGrouped).map(m => ({
        nome: m.nome,
        quantita: '-',
        prezzoUnitario: '-',
        costoTrasporto: '-',
        costoTotale: m.costoTotale
      })),
      ...Object.values(materialiFissiGrouped).map(m => ({
        nome: m.nome,
        quantita: formatCurrency(m.quantitaTotale),
        prezzoUnitario: '-',
        costoTrasporto: '-',
        costoTotale: m.totale
      }))
    ];

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 15px; line-height: 1.4;">
        <div style="display: flex; align-items: center; margin-bottom: 20px; border-bottom: 3px solid #000; padding-bottom: 15px;">
          <img src="${logo}" alt="Logo" style="height: 90px; margin-right: 20px;" />
          <h1 style="color: #000; margin: 0; flex: 1; text-align: center; font-size: 24px; font-weight: bold;">
            CONTABILITÀ CANTIERE
          </h1>
        </div>

        <div style="margin: 10px 0; font-size: 12px; background: #f5f5f5; padding: 10px; border-radius: 6px;">
          <p style="margin: 3px 0;"><strong>Nome Cantiere:</strong> ${cantiere.nomeCommessa || 'N/A'}</p>
          ${cantiere.contratto ? `<p style="margin: 3px 0;"><strong>Contratto:</strong> ${cantiere.contratto}</p>` : ''}
          ${cantiere.luogo || cantiere.indirizzo ? `<p style="margin: 3px 0;"><strong>Luogo:</strong> ${cantiere.luogo || cantiere.indirizzo}</p>` : ''}
          ${cantiere.impresa ? `<p style="margin: 3px 0;"><strong>Impresa:</strong> ${cantiere.impresa}</p>` : ''}
          <p style="margin: 3px 0;"><strong>Periodo:</strong> dal ${new Date(periodo.inizio).toLocaleDateString('it-IT')} al ${new Date(periodo.fine).toLocaleDateString('it-IT')}</p>
        </div>

        <div style="margin: 15px 0;">
          <h3 style="color: #000; font-size: 14px; margin: 10px 0 5px 0;">Presenti e Manodopera</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #e0e0e0;">
                <th style="border: 1px solid #999; padding: 6px; text-align: left;">Dipendente</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">Ore</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">€/h</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: right;">Totale</th>
              </tr>
            </thead>
            <tbody>
              ${personale.map(p => {
                if (p.isSpeseVarie) {
                  return `
                    <tr style="background: #fff3e0;">
                      <td style="border: 1px solid #999; padding: 6px; font-weight: bold;">${p.nome}</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: center; color: #999;">—</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: center; color: #999;">—</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: right; font-weight: bold;">€${formatCurrency(p.totalCosto)}</td>
                    </tr>`;
                }
                return `
                  <tr>
                    <td style="border: 1px solid #999; padding: 6px;">${p.cognome} ${p.nome}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: center;">${formatCurrency(p.totalOre)}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: center;">€${formatCurrency(p.costoOrario)}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: right;">€${formatCurrency(p.totalCosto)}</td>
                  </tr>`;
              }).join('')}
              <tr style="background: #d0d0d0; font-weight: bold;">
                <td colspan="3" style="border: 1px solid #999; padding: 5px;">TOTALE PRESENTI E MANODOPERA:</td>
                <td style="border: 1px solid #999; padding: 5px; text-align: right;">€${formatCurrency(totali.personale)}</td>
              </tr>
            </tbody>
          </table>

          ${mezzi && mezzi.length > 0 ? `
          <h3 style="color: #000; font-size: 14px; margin: 20px 0 5px 0;">Mezzi e Attrezzature</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #e0e0e0;">
                <th style="border: 1px solid #999; padding: 6px; text-align: left;">Nome Mezzo</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: left;">Targa</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">Ore</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">€/h</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: right;">Totale</th>
              </tr>
            </thead>
            <tbody>
              ${mezzi.map(m => {
                if (m.isSpeseVarie) {
                  return `
                    <tr style="background: #fff3e0;">
                      <td style="border: 1px solid #999; padding: 6px; font-weight: bold;">${m.nome}</td>
                      <td style="border: 1px solid #999; padding: 6px; color: #999;">—</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: center; color: #999;">—</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: center; color: #999;">—</td>
                      <td style="border: 1px solid #999; padding: 6px; text-align: right; font-weight: bold;">€${formatCurrency(m.totalCosto)}</td>
                    </tr>`;
                }
                return `
                  <tr>
                    <td style="border: 1px solid #999; padding: 6px;">${m.nome}</td>
                    <td style="border: 1px solid #999; padding: 6px;">${m.targa || 'N/A'}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: center;">${formatCurrency(m.totalOre)}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: center;">€${formatCurrency(m.costoOrario)}</td>
                    <td style="border: 1px solid #999; padding: 6px; text-align: right;">€${formatCurrency(m.totalCosto)}</td>
                  </tr>`;
              }).join('')}
              <tr style="background: #d0d0d0; font-weight: bold;">
                <td colspan="4" style="border: 1px solid #999; padding: 5px;">TOTALE MEZZI E ATTREZZATURE:</td>
                <td style="border: 1px solid #999; padding: 5px; text-align: right;">€${formatCurrency(totali.mezzi)}</td>
              </tr>
            </tbody>
          </table>
          ` : ''}

          ${materialiCombinati.length > 0 ? `
          <h3 style="color: #000; font-size: 14px; margin: 20px 0 5px 0;">Materiali Utilizzati</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #e0e0e0;">
                <th style="border: 1px solid #999; padding: 6px; text-align: left;">Nome Materiale</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">Quantità</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">Prezzo Unit.</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">Trasporto</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: right;">Costo Totale</th>
              </tr>
            </thead>
            <tbody>
              ${materialiCombinati.map(m => `
                <tr>
                  <td style="border: 1px solid #999; padding: 6px;">${m.nome}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${m.quantita}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${m.prezzoUnitario}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${m.costoTrasporto}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: right;">€${formatCurrency(m.costoTotale)}</td>
                </tr>
              `).join('')}
              <tr style="background: #d0d0d0; font-weight: bold;">
                <td colspan="4" style="border: 1px solid #999; padding: 5px;">TOTALE MATERIALI:</td>
                <td style="border: 1px solid #999; padding: 5px; text-align: right;">€${formatCurrency(totali.materialiVariabili + totali.materialiFissi)}</td>
              </tr>
            </tbody>
          </table>
          ` : ''}

          ${(reportData.speseVarie || []).length > 0 ? `
          <h3 style="color: #e65100; font-size: 14px; margin: 20px 0 5px 0;">💳 Spese Varie</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <thead>
              <tr style="background: #fff3e0;">
                <th style="border: 1px solid #999; padding: 6px; text-align: left;">Descrizione</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: center;">Data</th>
                <th style="border: 1px solid #999; padding: 6px; text-align: right;">Importo</th>
              </tr>
            </thead>
            <tbody>
              ${(reportData.speseVarie || []).map(s => `
                <tr style="background: #fffde7;">
                  <td style="border: 1px solid #999; padding: 6px; font-weight: bold;">💳 ${s.descrizione}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: center;">${new Date(s.data).toLocaleDateString('it-IT')}</td>
                  <td style="border: 1px solid #999; padding: 6px; text-align: right; font-weight: bold;">€${formatCurrency(s.importo)}</td>
                </tr>
              `).join('')}
              <tr style="background: #ffe0b2; font-weight: bold;">
                <td colspan="2" style="border: 1px solid #999; padding: 5px;">TOTALE SPESE VARIE:</td>
                <td style="border: 1px solid #999; padding: 5px; text-align: right;">€${formatCurrency(totali.speseVarie)}</td>
              </tr>
            </tbody>
          </table>
          ` : ''}
        </div>

        <div style="margin: 30px 0;">
          <h2 style="color: #000; font-size: 16px; margin: 10px 0; padding-bottom: 3px;">Riepilogo Economico</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0;">
            <tbody>
              <tr style="background: #f5f5f5;">
                <td style="border: 1px solid #999; padding: 8px;">Ricavi Totali</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.ricavi)}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #999; padding: 8px;">Costi Personale</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.personale)}</td>
              </tr>
              <tr style="background: #f5f5f5;">
                <td style="border: 1px solid #999; padding: 8px;">Costi Mezzi</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.mezzi)}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #999; padding: 8px;">Materiali Variabili</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.materialiVariabili)}</td>
              </tr>
              <tr style="background: #f5f5f5;">
                <td style="border: 1px solid #999; padding: 8px;">Materiali Fissi</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.materialiFissi)}</td>
              </tr>
              ${(totali.speseVarie || 0) > 0 ? `
              <tr style="background: #fff3e0;">
                <td style="border: 1px solid #999; padding: 8px;">💳 Spese Varie</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right; font-weight: bold;">€${formatCurrency(totali.speseVarie)}</td>
              </tr>
              ` : ''}
              <tr style="background: #d0d0d0; font-weight: bold; border-top: 2px solid #999;">
                <td style="border: 1px solid #999; padding: 8px;">TOTALE COSTI</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.costi)}</td>
              </tr>
              <tr style="background: ${totali.margine >= 0 ? '#e8f5e9' : '#ffebee'}; font-weight: bold; border-top: 2px solid #999;">
                <td style="border: 1px solid #999; padding: 8px;">MARGINE OPERATIVO (${formatCurrency(totali.marginePercentuale)}%)</td>
                <td style="border: 1px solid #999; padding: 8px; text-align: right;">€${formatCurrency(totali.margine)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="page-footer">
          <p style="margin: 0; font-size: 10px;">Documento generato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT')}</p>
        </div>
      </div>
    `;
  };

  // ============================================================
  // EXCEL — fogli con stile per spese varie
  // ============================================================
  const downloadExcelFile = async (reportData, fileName) => {
    try {
      if (typeof window.ExcelJS === 'undefined') {
        await loadExcelJS();
      }

      const { cantiere, periodo, personale, lavorazioni, materialiVariabili, materialiFissi, mezzi, totali } = reportData;

      const workbook = new window.ExcelJS.Workbook();
      workbook.creator = 'Sistema Gestione Cantieri';
      workbook.created = new Date();
      workbook.modified = new Date();

      await createCopertinaWithLogo(workbook, cantiere, periodo, totali);

      const personaleSheet = workbook.addWorksheet('Personale');
      createPersonaleSheet(personaleSheet, personale, totali);

      const mezziSheet = workbook.addWorksheet('Mezzi');
      createMezziSheet(mezziSheet, mezzi, totali);

      const lavorazioniSheet = workbook.addWorksheet('Lavorazioni');
      createLavorazioniSheet(lavorazioniSheet, lavorazioni);

      const matVariabiliSheet = workbook.addWorksheet('Materiali Variabili');
      createMatVariabiliSheet(matVariabiliSheet, materialiVariabili, totali);

      const matFissiSheet = workbook.addWorksheet('Materiali Fissi');
      createMatFissiSheet(matFissiSheet, materialiFissi, totali);

      const ricaviSheet = workbook.addWorksheet('Ricavi');
      createRicaviSheet(ricaviSheet, reportData.ricavi || [], totali);

      // ✨ Foglio Spese Varie (solo se presenti)
      if ((reportData.speseVarie || []).length > 0) {
        const speseVarieSheet = workbook.addWorksheet('Spese Varie');
        createSpeseVarieSheet(speseVarieSheet, reportData.speseVarie, totali);
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      showNotification(`File Excel "${fileName}" generato con successo!`, 'success');
    } catch (error) {
      console.error('Errore nella generazione Excel:', error);
      downloadImprovedCSV(reportData, fileName.replace('.xlsx', '.csv'));
      showNotification('Errore Excel, generato file CSV alternativo', 'warning');
    }
  };

  // ============================================================
  // CSV — include sezione spese varie
  // ============================================================
  const downloadImprovedCSV = (reportData, fileName) => {
    const { cantiere, periodo, personale, lavorazioni, materialiVariabili, materialiFissi, mezzi, speseVarie, totali } = reportData;

    let csvContent = '';
    csvContent += `==============================================================\n`;
    csvContent += `           🏗️ REPORT CANTIERE 🏗️\n`;
    csvContent += `==============================================================\n\n`;
    csvContent += `CANTIERE: ${cantiere.nomeCommessa || cantiere.nomeCantiere || 'N/A'}\n`;
    csvContent += `PERIODO: ${new Date(periodo.inizio).toLocaleDateString('it-IT')} - ${new Date(periodo.fine).toLocaleDateString('it-IT')}\n`;
    csvContent += `GENERATO: ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT')}\n\n`;

    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `                    💰 RIEPILOGO ECONOMICO\n`;
    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `Ricavi Totali,€${formatCurrency(totali.ricavi)}\n`;
    csvContent += `Costi Personale,€${formatCurrency(totali.personale)}\n`;
    csvContent += `Costi Mezzi,€${formatCurrency(totali.mezzi)}\n`;
    csvContent += `Costi Materiali Variabili,€${formatCurrency(totali.materialiVariabili)}\n`;
    csvContent += `Costi Materiali Fissi,€${formatCurrency(totali.materialiFissi)}\n`;
    if ((totali.speseVarie || 0) > 0) {
      csvContent += `Spese Varie (non categ.),€${formatCurrency(totali.speseVarie)}\n`;
    }
    csvContent += `TOTALE COSTI,€${formatCurrency(totali.costi)}\n`;
    csvContent += `MARGINE,€${formatCurrency(totali.margine)}\n`;
    csvContent += `MARGINE %,${formatCurrency(totali.marginePercentuale)}%\n\n`;

    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `                        👷 PERSONALE\n`;
    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `Nome,Cognome,Incarico,Ore Totali,Costo Orario €,Costo Totale €\n`;
    personale.forEach(p => {
      if (p.isSpeseVarie) {
        csvContent += `"${p.nome}","","Spesa Varia","—","—",${formatCurrency(p.totalCosto)}\n`;
      } else {
        csvContent += `"${p.nome}","${p.cognome}","${p.incarico}",${formatCurrency(p.totalOre)},${formatCurrency(p.costoOrario)},${formatCurrency(p.totalCosto)}\n`;
      }
    });
    csvContent += `TOTALE,,,"${formatCurrency(personale.filter(p => !p.isSpeseVarie).reduce((sum, p) => sum + p.totalOre, 0))}",,${formatCurrency(totali.personale)}\n\n`;

    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `                    🚛 MEZZI E ATTREZZATURE\n`;
    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `Nome Mezzo,Targa,Proprietà,Ore Lavorate,Costo Orario €,Costo Totale €\n`;
    mezzi.forEach(m => {
      if (m.isSpeseVarie) {
        csvContent += `"${m.nome}","—","—","—","—",${formatCurrency(m.totalCosto)}\n`;
      } else {
        csvContent += `"${m.nome}","${m.targa || 'N/A'}","${m.proprieta || 'N/D'}",${formatCurrency(m.totalOre)},${formatCurrency(m.costoOrario)},${formatCurrency(m.totalCosto)}\n`;
      }
    });
    csvContent += `TOTALE,,,${formatCurrency(mezzi.filter(m => !m.isSpeseVarie).reduce((sum, m) => sum + m.totalOre, 0))},,${formatCurrency(totali.mezzi)}\n\n`;

    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `                    ⚒️ LAVORAZIONI EFFETTUATE\n`;
    csvContent += `═══════════════════════════════════════════════════════════\n`;
    csvContent += `Nome Lavorazione,Quantità,Descrizioni\n`;
    lavorazioni.forEach(l => {
      csvContent += `"${l.nome}",${formatCurrency(l.quantitaTotale)},"${Array.from(l.descrizioni).join('; ')}"\n`;
    });
    csvContent += `\n`;

    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `                📦 MATERIALI A COSTO VARIABILE\n`;
    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `Nome,Descrizione,Costo €,Data\n`;
    materialiVariabili.forEach(m => {
      csvContent += `"${m.nome}","${m.descrizione}",${formatCurrency(m.costo)},"${new Date(m.data).toLocaleDateString('it-IT')}"\n`;
    });
    csvContent += `TOTALE,,,${formatCurrency(totali.materialiVariabili)}\n\n`;

    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `                  🧱 MATERIALI A COSTO FISSO\n`;
    csvContent += `══════════════════════════════════════════════════════════\n`;
    csvContent += `Nome,Quantità,Prezzo Unitario €,Costo Trasporto €,Totale €,Data\n`;
    materialiFissi.forEach(m => {
      csvContent += `"${m.nome}",${formatCurrency(m.quantita)},${formatCurrency(m.prezzoUnitario)},${formatCurrency(m.costoTrasporto)},${formatCurrency(m.totale)},"${new Date(m.data).toLocaleDateString('it-IT')}"\n`;
    });
    csvContent += `TOTALE,,,,,${formatCurrency(totali.materialiFissi)}\n\n`;

    // ✨ SEZIONE SPESE VARIE NEL CSV
    if ((speseVarie || []).length > 0) {
      csvContent += `══════════════════════════════════════════════════════════\n`;
      csvContent += `                    💳 SPESE VARIE\n`;
      csvContent += `══════════════════════════════════════════════════════════\n`;
      csvContent += `Descrizione,Data,Importo €\n`;
      speseVarie.forEach(s => {
        csvContent += `"${s.descrizione}","${new Date(s.data).toLocaleDateString('it-IT')}",${formatCurrency(s.importo)}\n`;
      });
      csvContent += `TOTALE,,${formatCurrency(totali.speseVarie)}\n\n`;
    }

    csvContent += `==============================================================\n`;
    csvContent += `                    📊 FINE REPORT\n`;
    csvContent += `==============================================================\n`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification(`File CSV "${fileName}" generato con successo!`, 'success');
  };

  // ============================================================
  // HELPERS EXCELJS
  // ============================================================
  const loadExcelJS = () => {
    return new Promise((resolve, reject) => {
      if (window.ExcelJS) { resolve(); return; }
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.3.0/exceljs.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Impossibile caricare ExcelJS'));
      document.head.appendChild(script);
    });
  };

  const createCopertinaWithLogo = async (workbook, cantiere, periodo, totali) => {
    try {
      const copertina = workbook.addWorksheet('Copertina');
      const logoBase64 = await getLogoBase64();
      if (logoBase64) {
        const logoBuffer = base64ToBuffer(logoBase64);
        const logoId = workbook.addImage({ buffer: logoBuffer, extension: 'png' });
        copertina.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 200, height: 100 } });
      }
      copertina.getCell('A6').value = '🏗️ REPORT CANTIERE 🏗️';
      copertina.getCell('A6').font = { size: 16, bold: true, color: { argb: 'FF0066CC' } };
      copertina.getCell('A6').alignment = { horizontal: 'center' };
      copertina.mergeCells('A6:D6');
      copertina.getCell('A10').value = '📋 INFORMAZIONI CANTIERE';
      copertina.getCell('A10').font = { size: 14, bold: true, color: { argb: 'FF0066CC' } };
      copertina.getCell('A12').value = `Nome Cantiere: ${cantiere.nomeCommessa || cantiere.nomeCantiere || 'N/A'}`;
      copertina.getCell('A13').value = `Contratto: ${cantiere.contratto || 'N/A'}`;
      copertina.getCell('A14').value = `Luogo: ${cantiere.luogo || cantiere.indirizzo || 'N/A'}`;
      copertina.getCell('A15').value = `Impresa: ${cantiere.impresa || 'N/A'}`;
      copertina.getCell('A16').value = `Periodo: ${new Date(periodo.inizio).toLocaleDateString('it-IT')} - ${new Date(periodo.fine).toLocaleDateString('it-IT')}`;
      copertina.getCell('A18').value = '💰 RIEPILOGO ECONOMICO';
      copertina.getCell('A18').font = { size: 14, bold: true, color: { argb: 'FF0066CC' } };
      copertina.getCell('A20').value = `Ricavi Totali: € ${formatCurrency(totali.ricavi)}`;
      copertina.getCell('A21').value = `Costi Totali: € ${formatCurrency(totali.costi)}`;
      copertina.getCell('A22').value = `Margine: € ${formatCurrency(totali.margine)}`;
      copertina.getCell('A23').value = `Margine %: ${formatCurrency(totali.marginePercentuale)}%`;
      const margineColor = totali.margine >= 0 ? 'FF00AA00' : 'FFAA0000';
      copertina.getCell('A22').font = { color: { argb: margineColor }, bold: true };
      copertina.getCell('A23').font = { color: { argb: margineColor }, bold: true };
      copertina.getColumn('A').width = 60;
      copertina.getColumn('B').width = 20;
      copertina.getColumn('C').width = 20;
      copertina.getColumn('D').width = 20;
    } catch (error) {
      console.error('Errore nella creazione copertina:', error);
    }
  };

  const base64ToBuffer = (base64) => {
    const binaryString = window.atob(base64.split(',')[1]);
    const buffer = new ArrayBuffer(binaryString.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binaryString.length; i++) {
      view[i] = binaryString.charCodeAt(i);
    }
    return buffer;
  };

  // ✨ Helper per applicare stile arancione alle righe spese varie
  const applySpeseVarieStyle = (sheet, row, numCols) => {
    for (let col = 1; col <= numCols; col++) {
      sheet.getCell(row, col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } };
      sheet.getCell(row, col).font = { bold: true };
    }
  };

  const createPersonaleSheet = (sheet, personale, totali) => {
    sheet.getCell('A1').value = '=== PERSONALE IMPIEGATO ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:F1');
    sheet.getCell('A3').value = 'Nome';
    sheet.getCell('B3').value = 'Cognome';
    sheet.getCell('C3').value = 'Incarico';
    sheet.getCell('D3').value = 'Ore Totali';
    sheet.getCell('E3').value = 'Costo Orario €';
    sheet.getCell('F3').value = 'Costo Totale €';
    for (let col = 1; col <= 6; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    }
    personale.forEach((p, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = p.nome;
      sheet.getCell(row, 2).value = p.cognome || '';
      sheet.getCell(row, 3).value = p.isSpeseVarie ? 'Spesa Varia' : p.incarico;
      sheet.getCell(row, 4).value = p.isSpeseVarie ? '—' : p.totalOre;
      sheet.getCell(row, 5).value = p.isSpeseVarie ? '—' : p.costoOrario;
      sheet.getCell(row, 6).value = p.totalCosto;
      if (!p.isSpeseVarie) {
        sheet.getCell(row, 4).numFmt = '#,##0.00';
        sheet.getCell(row, 5).numFmt = '€#,##0.00';
      }
      sheet.getCell(row, 6).numFmt = '€#,##0.00';
      if (p.isSpeseVarie) applySpeseVarieStyle(sheet, row, 6);
    });
    const totalRow = personale.length + 5;
    sheet.getCell(totalRow, 1).value = '=== TOTALE ===';
    sheet.getCell(totalRow, 1).font = { bold: true };
    sheet.getCell(totalRow, 4).value = personale.filter(p => !p.isSpeseVarie).reduce((sum, p) => sum + p.totalOre, 0);
    sheet.getCell(totalRow, 4).font = { bold: true };
    sheet.getCell(totalRow, 4).numFmt = '#,##0.00';
    sheet.getCell(totalRow, 6).value = totali.personale;
    sheet.getCell(totalRow, 6).font = { bold: true };
    sheet.getCell(totalRow, 6).numFmt = '€#,##0.00';
    sheet.getColumn('A').width = 18;
    sheet.getColumn('B').width = 18;
    sheet.getColumn('C').width = 25;
    sheet.getColumn('D').width = 15;
    sheet.getColumn('E').width = 18;
    sheet.getColumn('F').width = 18;
  };

  const createMezziSheet = (sheet, mezzi, totali) => {
    sheet.getCell('A1').value = '=== MEZZI E ATTREZZATURE ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:F1');
    sheet.getCell('A3').value = 'Nome Mezzo';
    sheet.getCell('B3').value = 'Targa';
    sheet.getCell('C3').value = 'Proprietà';
    sheet.getCell('D3').value = 'Ore Lavorate';
    sheet.getCell('E3').value = 'Costo Orario €';
    sheet.getCell('F3').value = 'Costo Totale €';
    for (let col = 1; col <= 6; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    }
    mezzi.forEach((m, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = m.nome;
      sheet.getCell(row, 2).value = m.isSpeseVarie ? '—' : (m.targa || 'N/A');
      sheet.getCell(row, 3).value = m.isSpeseVarie ? '—' : (m.proprieta || 'N/D');
      sheet.getCell(row, 4).value = m.isSpeseVarie ? '—' : m.totalOre;
      sheet.getCell(row, 5).value = m.isSpeseVarie ? '—' : m.costoOrario;
      sheet.getCell(row, 6).value = m.totalCosto;
      if (!m.isSpeseVarie) {
        sheet.getCell(row, 4).numFmt = '#,##0.00';
        sheet.getCell(row, 5).numFmt = '€#,##0.00';
      }
      sheet.getCell(row, 6).numFmt = '€#,##0.00';
      if (m.isSpeseVarie) applySpeseVarieStyle(sheet, row, 6);
    });
    const totalRow = mezzi.length + 5;
    sheet.getCell(totalRow, 1).value = '=== TOTALE ===';
    sheet.getCell(totalRow, 1).font = { bold: true };
    sheet.getCell(totalRow, 4).value = mezzi.filter(m => !m.isSpeseVarie).reduce((sum, m) => sum + m.totalOre, 0);
    sheet.getCell(totalRow, 4).font = { bold: true };
    sheet.getCell(totalRow, 4).numFmt = '#,##0.00';
    sheet.getCell(totalRow, 6).value = totali.mezzi;
    sheet.getCell(totalRow, 6).font = { bold: true };
    sheet.getCell(totalRow, 6).numFmt = '€#,##0.00';
    sheet.getColumn('A').width = 30;
    sheet.getColumn('B').width = 15;
    sheet.getColumn('C').width = 15;
    sheet.getColumn('D').width = 15;
    sheet.getColumn('E').width = 18;
    sheet.getColumn('F').width = 18;
  };

  const createLavorazioniSheet = (sheet, lavorazioni) => {
    sheet.getCell('A1').value = '=== LAVORAZIONI EFFETTUATE ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:C1');
    sheet.getCell('A3').value = 'Nome Lavorazione';
    sheet.getCell('B3').value = 'Quantità Totale';
    sheet.getCell('C3').value = 'Descrizioni';
    for (let col = 1; col <= 3; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    }
    lavorazioni.forEach((l, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = l.nome;
      sheet.getCell(row, 2).value = l.quantitaTotale;
      sheet.getCell(row, 2).numFmt = '#,##0.00';
      sheet.getCell(row, 3).value = Array.from(l.descrizioni).join('; ');
    });
    sheet.getColumn('A').width = 30;
    sheet.getColumn('B').width = 20;
    sheet.getColumn('C').width = 50;
  };

  const createMatVariabiliSheet = (sheet, materialiVariabili, totali) => {
    sheet.getCell('A1').value = '=== MATERIALI A COSTO VARIABILE ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:D1');
    sheet.getCell('A3').value = 'Nome';
    sheet.getCell('B3').value = 'Descrizione';
    sheet.getCell('C3').value = 'Costo €';
    sheet.getCell('D3').value = 'Data';
    for (let col = 1; col <= 4; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    }
    materialiVariabili.forEach((m, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = m.nome;
      sheet.getCell(row, 2).value = m.descrizione || '';
      sheet.getCell(row, 3).value = m.costo;
      sheet.getCell(row, 3).numFmt = '€#,##0.00';
      sheet.getCell(row, 4).value = new Date(m.data).toLocaleDateString('it-IT');
      if (m.isSpeseVarie) applySpeseVarieStyle(sheet, row, 4);
    });
    const totalRow = materialiVariabili.length + 5;
    sheet.getCell(totalRow, 1).value = '=== TOTALE ===';
    sheet.getCell(totalRow, 1).font = { bold: true };
    sheet.getCell(totalRow, 3).value = totali.materialiVariabili;
    sheet.getCell(totalRow, 3).font = { bold: true };
    sheet.getCell(totalRow, 3).numFmt = '€#,##0.00';
    sheet.getColumn('A').width = 30;
    sheet.getColumn('B').width = 35;
    sheet.getColumn('C').width = 18;
    sheet.getColumn('D').width = 15;
  };

  const createMatFissiSheet = (sheet, materialiFissi, totali) => {
    sheet.getCell('A1').value = '=== MATERIALI A COSTO FISSO ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:F1');
    sheet.getCell('A3').value = 'Nome';
    sheet.getCell('B3').value = 'Quantità';
    sheet.getCell('C3').value = 'Prezzo Unitario €';
    sheet.getCell('D3').value = 'Costo Trasporto €';
    sheet.getCell('E3').value = 'Totale €';
    sheet.getCell('F3').value = 'Data';
    for (let col = 1; col <= 6; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    }
    materialiFissi.forEach((m, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = m.nome;
      sheet.getCell(row, 2).value = m.quantita;
      sheet.getCell(row, 2).numFmt = '#,##0.00';
      sheet.getCell(row, 3).value = m.prezzoUnitario;
      sheet.getCell(row, 3).numFmt = '€#,##0.00';
      sheet.getCell(row, 4).value = m.costoTrasporto;
      sheet.getCell(row, 4).numFmt = '€#,##0.00';
      sheet.getCell(row, 5).value = m.totale;
      sheet.getCell(row, 5).numFmt = '€#,##0.00';
      sheet.getCell(row, 6).value = new Date(m.data).toLocaleDateString('it-IT');
    });
    const totalRow = materialiFissi.length + 5;
    sheet.getCell(totalRow, 1).value = '=== TOTALE ===';
    sheet.getCell(totalRow, 1).font = { bold: true };
    sheet.getCell(totalRow, 5).value = totali.materialiFissi;
    sheet.getCell(totalRow, 5).font = { bold: true };
    sheet.getCell(totalRow, 5).numFmt = '€#,##0.00';
    sheet.getColumn('A').width = 30;
    sheet.getColumn('B').width = 12;
    sheet.getColumn('C').width = 20;
    sheet.getColumn('D').width = 20;
    sheet.getColumn('E').width = 18;
    sheet.getColumn('F').width = 15;
  };

  const createRicaviSheet = (sheet, ricavi, totali) => {
    sheet.getCell('A1').value = '=== RICAVI ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:E1');
    sheet.getCell('A3').value = 'Descrizione';
    sheet.getCell('B3').value = 'Tipo';
    sheet.getCell('C3').value = 'Importo €';
    sheet.getCell('D3').value = 'Data';
    sheet.getCell('E3').value = 'Note';
    for (let col = 1; col <= 5; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    }
    ricavi.forEach((r, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = r.descrizione || 'Ricavo';
      sheet.getCell(row, 2).value = r.tipo || 'Generale';
      sheet.getCell(row, 3).value = parseFloat(r.importo || 0);
      sheet.getCell(row, 3).numFmt = '€#,##0.00';
      sheet.getCell(row, 4).value = r.data ? new Date(r.data).toLocaleDateString('it-IT') : '';
      sheet.getCell(row, 5).value = r.note || '';
    });
    const totalRow = ricavi.length + 5;
    sheet.getCell(totalRow, 1).value = '=== TOTALE ===';
    sheet.getCell(totalRow, 1).font = { bold: true };
    sheet.getCell(totalRow, 3).value = totali.ricavi;
    sheet.getCell(totalRow, 3).font = { bold: true };
    sheet.getCell(totalRow, 3).numFmt = '€#,##0.00';
    sheet.getColumn('A').width = 35;
    sheet.getColumn('B').width = 20;
    sheet.getColumn('C').width = 18;
    sheet.getColumn('D').width = 15;
    sheet.getColumn('E').width = 30;
  };

  // ✨ NUOVO — Foglio Excel dedicato alle Spese Varie non categorizzate
  const createSpeseVarieSheet = (sheet, speseVarie, totali) => {
    sheet.getCell('A1').value = '=== SPESE VARIE (non categorizzate) ===';
    sheet.getCell('A1').font = { bold: true };
    sheet.mergeCells('A1:C1');
    sheet.getCell('A3').value = 'Descrizione';
    sheet.getCell('B3').value = 'Data';
    sheet.getCell('C3').value = 'Importo €';
    for (let col = 1; col <= 3; col++) {
      const cell = sheet.getCell(3, col);
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE0B2' } };
    }
    speseVarie.forEach((s, index) => {
      const row = index + 4;
      sheet.getCell(row, 1).value = s.descrizione;
      sheet.getCell(row, 2).value = new Date(s.data).toLocaleDateString('it-IT');
      sheet.getCell(row, 3).value = s.importo;
      sheet.getCell(row, 3).numFmt = '€#,##0.00';
      for (let col = 1; col <= 3; col++) {
        sheet.getCell(row, col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
      }
    });
    const totalRow = speseVarie.length + 5;
    sheet.getCell(totalRow, 1).value = '=== TOTALE ===';
    sheet.getCell(totalRow, 1).font = { bold: true };
    sheet.getCell(totalRow, 3).value = totali.speseVarie || 0;
    sheet.getCell(totalRow, 3).font = { bold: true };
    sheet.getCell(totalRow, 3).numFmt = '€#,##0.00';
    sheet.getColumn('A').width = 40;
    sheet.getColumn('B').width = 15;
    sheet.getColumn('C').width = 18;
  };

  // ============================================================
  // DOWNLOAD PDF
  // ============================================================
  const downloadPDF = (htmlContent, fileName) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showNotification('Impossibile aprire finestra di stampa. Controlla i popup.', 'error');
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${fileName}</title>
        <meta charset="utf-8">
        <style>
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; font-size: 12px; }
          @page { margin: 15mm; size: A4; }
          @media print {
            body { margin: 0; padding: 0; }
            .page-footer { position: fixed; bottom: 0; width: 100%; border-top: 1px solid #ccc; padding-top: 5px; }
            .section-break { page-break-inside: avoid; }
          }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #999; padding: 6px; }
          th { background: #e0e0e0; font-weight: bold; }
        </style>
      </head>
      <body>
        ${htmlContent}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 1000);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Notifiche */}
      {notification.show && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          padding: '15px 20px', borderRadius: '8px', color: 'white', fontWeight: 'bold',
          background: notification.type === 'success' ? '#28a745' :
                      notification.type === 'error' ? '#dc3545' :
                      notification.type === 'warning' ? '#ffc107' : '#17a2b8',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
          {notification.message}
        </div>
      )}

      <h1 style={{ color: '#2c3e50', marginBottom: '30px', borderBottom: '3px solid #3498db', paddingBottom: '10px' }}>
        📊 Report Cantieri
      </h1>

      {/* Form Selezione */}
      <div style={{
        background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
        padding: '25px', borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)', marginBottom: '30px'
      }}>
        <h2 style={{ color: '#495057', marginBottom: '20px' }}>🔍 Seleziona Parametri</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
              🏗️ Cantiere
            </label>
            <input
              type="text"
              placeholder="Cerca cantiere..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ced4da', marginBottom: '8px', boxSizing: 'border-box' }}
            />
            <select
              value={selectedCantiere}
              onChange={(e) => setSelectedCantiere(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ced4da', boxSizing: 'border-box' }}
            >
              <option value="">-- Seleziona Cantiere --</option>
              {filteredCantieri.map(c => (
                <option key={c.id} value={c.id}>
                  {c.nomeCommessa} {c.luogo ? `(${c.luogo})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
              📅 Data Inizio
            </label>
            <input
              type="date"
              value={dataInizio}
              onChange={(e) => setDataInizio(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ced4da', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
              📅 Data Fine
            </label>
            <input
              type="date"
              value={dataFine}
              onChange={(e) => setDataFine(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ced4da', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={caricaRapportini}
            disabled={isLoading}
            style={{
              padding: '14px 40px', fontSize: '16px', fontWeight: 'bold',
              background: isLoading ? '#6c757d' : '#007bff', color: 'white',
              border: 'none', borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease'
            }}
          >
            {isLoading ? '⏳ Caricamento...' : '🔍 Carica Dati'}
          </button>
        </div>
      </div>

      {/* Anteprima Dati */}
      {reportData && (
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            padding: '20px', borderRadius: '12px 12px 0 0', borderBottom: '3px solid #2196f3'
          }}>
            <h2 style={{ color: '#1565c0', margin: '0 0 10px 0' }}>📋 Anteprima Dati</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div><strong>Cantiere:</strong> {reportData.cantiere.nomeCommessa}</div>
              <div><strong>Periodo:</strong> {new Date(reportData.periodo.inizio).toLocaleDateString('it-IT')} - {new Date(reportData.periodo.fine).toLocaleDateString('it-IT')}</div>
              <div><strong>Rapportini:</strong> {rapportini.length}</div>
            </div>
          </div>

          <div style={{ background: 'white', padding: '25px', borderRadius: '0 0 12px 12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '25px' }}>
              {/* Card Ricavi */}
              <div style={{ background: 'linear-gradient(135deg, #d4edda, #c3e6cb)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #28a745' }}>
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#155724' }}>€{formatCurrency(reportData.totali.ricavi)}</div>
                <div style={{ color: '#155724', fontSize: '0.9em', fontWeight: 'bold' }}>💰 RICAVI</div>
              </div>
              {/* Card Personale */}
              <div style={{ background: 'linear-gradient(135deg, #cce5ff, #b8daff)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #007bff' }}>
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#004085' }}>€{formatCurrency(reportData.totali.personale)}</div>
                <div style={{ color: '#004085', fontSize: '0.9em', fontWeight: 'bold' }}>👷 PERSONALE</div>
              </div>
              {/* Card Mezzi */}
              <div style={{ background: 'linear-gradient(135deg, #fff3cd, #ffeeba)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #ffc107' }}>
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#856404' }}>€{formatCurrency(reportData.totali.mezzi)}</div>
                <div style={{ color: '#856404', fontSize: '0.9em', fontWeight: 'bold' }}>🚛 MEZZI</div>
              </div>
              {/* Card Materiali */}
              <div style={{ background: 'linear-gradient(135deg, #f8d7da, #f5c6cb)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #dc3545' }}>
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#721c24' }}>€{formatCurrency(reportData.totali.materialiVariabili + reportData.totali.materialiFissi)}</div>
                <div style={{ color: '#721c24', fontSize: '0.9em', fontWeight: 'bold' }}>📦 MATERIALI</div>
              </div>
              {/* ✨ Card Spese Varie (solo se presenti) */}
              {(reportData.totali.speseVarie || 0) > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #ff9800' }}>
                  <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#e65100' }}>€{formatCurrency(reportData.totali.speseVarie)}</div>
                  <div style={{ color: '#e65100', fontSize: '0.9em', fontWeight: 'bold' }}>💳 SPESE VARIE</div>
                  <div style={{ color: '#bf360c', fontSize: '0.75em', marginTop: '4px' }}>
                    {(reportData.speseVarie || []).length} voci non categorizzate
                  </div>
                </div>
              )}
              {/* Card Margine */}
              <div style={{
                background: `linear-gradient(135deg, ${reportData.totali.margine >= 0 ? '#cce5ff, #b8daff' : '#f1aeb5, #f1aeb5'})`,
                padding: '15px', borderRadius: '8px', textAlign: 'center',
                border: `2px solid ${reportData.totali.margine >= 0 ? '#007bff' : '#dc3545'}`
              }}>
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: reportData.totali.margine >= 0 ? '#004085' : '#721c24' }}>
                  €{formatCurrency(reportData.totali.margine)}
                </div>
                <div style={{ color: reportData.totali.margine >= 0 ? '#004085' : '#721c24', fontSize: '0.9em', fontWeight: 'bold' }}>
                  MARGINE ({formatCurrency(reportData.totali.marginePercentuale)}%)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sezione Generazione Report */}
      {reportData && (
        <div style={{
          background: 'linear-gradient(135deg, #fff3cd, #ffeaa7)',
          padding: '25px', borderRadius: '12px', border: '3px solid #ffc107'
        }}>
          <h2 style={{ color: '#856404', marginBottom: '20px', textAlign: 'center' }}>📄 Genera Report</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <button
              onClick={generaGiornaleLavori}
              style={{
                padding: '20px', background: 'linear-gradient(135deg, #007bff, #0056b3)',
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
                boxShadow: '0 4px 8px rgba(0,123,255,0.3)', textAlign: 'center'
              }}
            >
              <div>📋 Giornale dei Lavori</div>
              <small style={{ opacity: 0.9, display: 'block', marginTop: '5px' }}>PDF senza importi economici</small>
            </button>
            <button
              onClick={generaContabilita}
              style={{
                padding: '20px', background: 'linear-gradient(135deg, #28a745, #1e7e34)',
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
                boxShadow: '0 4px 8px rgba(40,167,69,0.3)', textAlign: 'center'
              }}
            >
              <div>💰 Contabilità</div>
              <small style={{ opacity: 0.9, display: 'block', marginTop: '5px' }}>PDF con costi, ricavi e margini</small>
            </button>
            <button
              onClick={generaExcel}
              style={{
                padding: '20px', background: 'linear-gradient(135deg, #17a2b8, #117a8b)',
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
                boxShadow: '0 4px 8px rgba(23,162,184,0.3)', textAlign: 'center'
              }}
            >
              <div>📊 Export Excel/CSV</div>
              <small style={{ opacity: 0.9, display: 'block', marginTop: '5px' }}>Excel dettagliato</small>
            </button>
          </div>

          <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255,255,255,0.7)', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>ℹ️ Informazioni sui Report</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404' }}>
              <li><strong>Giornale dei Lavori:</strong> PDF con logo aziendale, lista dipendenti, lavorazioni e materiali (senza importi economici)</li>
              <li><strong>Contabilità:</strong> PDF con logo aziendale, analisi completa di costi, ricavi e marginalità — include le spese varie con sfondo arancione</li>
              <li><strong>Excel:</strong> Foglio separato "Spese Varie" per le voci non categorizzate; righe arancioni nelle tabelle per quelle allocate</li>
            </ul>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: '40px', padding: '20px', background: '#e9ecef', borderRadius: '8px', textAlign: 'center', color: '#6c757d' }}>
        <p style={{ margin: 0, fontStyle: 'italic' }}>
          Sistema Report Cantieri — Gestione completa di Giornale Lavori e Contabilità<br />
          Tutti i calcoli sono basati sui dati inseriti nei rapportini del periodo selezionato
        </p>
      </div>
    </div>
  );
};

export default ReportCantieriManagement;